// PaySuccessPage — user landed here after Stripe Checkout.
// Webhook is the source of truth. Poll our own /payments/session/{id}
// endpoint until it flips out of 'open' (paid | expired | canceled | failed).
import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { CheckCircle, XCircle, Spinner } from '@phosphor-icons/react';
import { paymentsAPI } from '../lib/paymentsApi';

const POLL_MS = 2000;
const MAX_POLLS = 20; // ~40 s window for the webhook to catch up.

function eventDetailPath(kind, id) {
  if (kind === 'tournament')  return `/tournaments/${id}`;
  if (kind === 'round_robin') return `/round-robins/${id}`;
  if (kind === 'club_event')  return `/club-events/${id}`;
  return '/events';
}

export default function PaySuccessPage() {
  const [sp] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = sp.get('session_id');
  const eventKindQ = sp.get('event_kind');
  const eventIdQ = sp.get('event_id');
  const [state, setState] = useState({ phase: 'loading', session: null, error: null });

  useEffect(() => {
    if (!sessionId) {
      setState({ phase: 'error', error: 'Missing session id.' });
      return;
    }
    let cancelled = false;
    let n = 0;
    let timer = null;
    const tick = async () => {
      n += 1;
      try {
        const r = await paymentsAPI.getSession(sessionId);
        if (cancelled) return;
        const st = r.data?.status;
        if (st === 'paid') {
          setState({ phase: 'paid', session: r.data });
          return;
        }
        if (st && st !== 'open' && st !== 'creating') {
          setState({ phase: 'terminal', session: r.data });
          return;
        }
        // Still open → keep polling.
        if (n >= MAX_POLLS) {
          setState({ phase: 'timeout', session: r.data });
          return;
        }
        timer = setTimeout(tick, POLL_MS);
      } catch (e) {
        if (cancelled) return;
        const detail = e.response?.data?.detail;
        setState({ phase: 'error',
                   error: typeof detail === 'string' ? detail : detail?.message || 'Could not load session.' });
      }
    };
    tick();
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [sessionId]);

  const kind = state.session?.event_kind || eventKindQ;
  const evId = state.session?.event_id || eventIdQ;
  const backTo = kind && evId ? eventDetailPath(kind, evId) : '/events';

  if (state.phase === 'error') {
    return (
      <div className="max-w-md mx-auto py-10 px-4 text-center" data-testid="pay-success-error">
        <XCircle size={40} weight="duotone" className="mx-auto text-red-500" />
        <h1 className="mt-3 text-lg font-serif font-semibold text-[#013523]">Something went wrong</h1>
        <p className="mt-1 text-sm text-[#6a766e]">{state.error}</p>
        <Link to="/events" className="mt-4 inline-block text-sm text-[#013523] underline">Back to events</Link>
      </div>
    );
  }
  if (state.phase === 'paid') {
    return (
      <div className="max-w-md mx-auto py-10 px-4 text-center" data-testid="pay-success-paid">
        <CheckCircle size={48} weight="fill" className="mx-auto text-[#015e3f]" />
        <h1 className="mt-3 text-2xl font-serif font-semibold text-[#013523]">You&rsquo;re paid up.</h1>
        <p className="mt-1 text-sm text-[#6a766e]">Thanks — see you at the event.</p>
        <button
          type="button"
          onClick={() => navigate(backTo, { replace: true })}
          data-testid="pay-success-back-btn"
          className="mt-5 inline-flex items-center justify-center rounded-full min-h-[44px] px-5 py-2 text-sm font-semibold bg-[#013523] text-white hover:bg-[#024029]"
        >
          Back to event
        </button>
      </div>
    );
  }
  if (state.phase === 'timeout' || state.phase === 'terminal') {
    // Terminal but not paid (expired/canceled/failed) OR polling exhausted.
    const isTimeout = state.phase === 'timeout';
    return (
      <div className="max-w-md mx-auto py-10 px-4 text-center" data-testid="pay-success-pending">
        <Spinner size={40} className="mx-auto text-[#013523] animate-spin" />
        <h1 className="mt-3 text-lg font-serif font-semibold text-[#013523]">
          {isTimeout ? 'Still confirming payment…' : 'Payment not confirmed'}
        </h1>
        <p className="mt-1 text-sm text-[#6a766e]">
          {isTimeout
            ? "Your bank is still confirming. Refresh in a minute — we'll update your status as soon as Stripe lets us know."
            : "The Checkout session ended without a completed payment. If you were charged, we'll reconcile automatically once Stripe confirms."}
        </p>
        <button
          type="button"
          onClick={() => navigate(backTo, { replace: true })}
          data-testid="pay-success-back-btn"
          className="mt-5 inline-flex items-center justify-center rounded-full min-h-[44px] px-5 py-2 text-sm font-semibold bg-[#013523] text-white hover:bg-[#024029]"
        >
          Back to event
        </button>
      </div>
    );
  }
  // loading
  return (
    <div className="max-w-md mx-auto py-10 px-4 text-center" data-testid="pay-success-loading">
      <Spinner size={40} className="mx-auto text-[#013523] animate-spin" />
      <h1 className="mt-3 text-lg font-serif font-semibold text-[#013523]">Confirming payment…</h1>
      <p className="mt-1 text-sm text-[#6a766e]">Waiting for Stripe to confirm your payment. This usually takes just a moment.</p>
    </div>
  );
}
